import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type LegalEntity } from '@/api/client';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { useToast } from '@/contexts/ToastContext';
import '@/components/CreatePersonModal/CreatePersonModal.css';

export interface LegalEntityCatalogFormProps {
  legalEntityId: string | null;
  createNonce: number;
  disabled?: boolean;
  /** Префилл названия при создании */
  nameSeed?: string | null;
  onSaved?: (entity: LegalEntity) => void;
  /** Перейти к созданию нового юрлица (из режима редактирования) */
  onRequestCreate?: () => void;
}

export function LegalEntityCatalogForm({
  legalEntityId,
  createNonce,
  disabled = false,
  nameSeed = null,
  onSaved,
  onRequestCreate,
}: LegalEntityCatalogFormProps) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const resetCreate = useCallback(() => {
    setName((nameSeed ?? '').trim());
    setNotes('');
  }, [nameSeed]);

  useEffect(() => {
    if (!legalEntityId) {
      resetCreate();
      return;
    }
    let cancelled = false;
    setLoading(true);
    void api.legalEntities
      .get(legalEntityId)
      .then((le) => {
        if (cancelled) return;
        setName(le.name);
        setNotes(le.notes?.trim() ?? '');
      })
      .catch((e) => {
        if (!cancelled) showError(e instanceof Error ? e.message : 'Не удалось загрузить юрлицо');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [legalEntityId, showError, resetCreate]);

  useEffect(() => {
    if (legalEntityId) return;
    resetCreate();
  }, [createNonce, legalEntityId, resetCreate]);

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['legal-entities'] });
    await queryClient.invalidateQueries({ queryKey: ['legalEntities'] });
    await queryClient.invalidateQueries({ queryKey: ['catalogManageSearch'] });
  }, [queryClient]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Укажите название юрлица');
      const body = {
        name: trimmed,
        notes: notes.trim() ? notes.trim() : null,
      };
      if (legalEntityId) return api.legalEntities.update(legalEntityId, body);
      return api.legalEntities.create(body);
    },
    onSuccess: async (entity) => {
      showSuccess(legalEntityId ? 'Юрлицо сохранено' : 'Юрлицо создано');
      await invalidate();
      onSaved?.(entity);
    },
    onError: (err: Error) => showError(err.message),
  });

  const busy = disabled || loading || saveMutation.isPending;

  return (
    <div className="create-person-modal__inner">
      {loading ? (
        <p className="create-person-modal__hint" aria-busy="true">
          Загрузка юрлица…
        </p>
      ) : (
        <>
          <Input
            placeholder="Название"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            fullWidth
            autoComplete="off"
          />
          <Input
            placeholder="Заметки"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            fullWidth
            autoComplete="off"
          />
        </>
      )}
      <div
        className={
          legalEntityId && onRequestCreate
            ? 'create-person-modal__actions'
            : 'create-person-modal__actions create-person-modal__actions--single'
        }
      >
        {legalEntityId && onRequestCreate ? (
          <Button
            fullWidth
            variant="outlined"
            type="button"
            disabled={busy}
            onClick={onRequestCreate}
          >
            Создать новое
          </Button>
        ) : null}
        <Button
          fullWidth
          variant="primary"
          type="button"
          disabled={busy}
          onClick={() => void saveMutation.mutateAsync()}
        >
          {saveMutation.isPending ? 'Сохранение…' : legalEntityId ? 'Сохранить' : 'Создать юрлицо'}
        </Button>
      </div>
    </div>
  );
}
